// Which rooms the open screens are subscribed to, and which cached queries belong to each one.
// The provider uses it to refetch a room's queries after a reconnection and to remove a space's
// cache when access to it is revoked. A cached query belongs to a room when its first key (the
// request URL, see the generated query keys) contains one of the room's fragments.
import type { RealtimeAccessRevoked } from '@batiflow/contracts';

export interface RoomScope {
  /** The space the room belongs to (a document room names the space of its document). */
  spaceId?: string;
  /** Extra URL fragments of the queries to refetch for this room (e.g. `/projects/{id}`). */
  fragments?: readonly string[];
}

interface Entry {
  room: string;
  scope: RoomScope;
}

/** URL fragment of the queries a room is about, from its name alone. */
export function defaultFragments(room: string): string[] {
  const [kind, id] = room.split(':');
  if (!id) return [];
  if (kind === 'space') return [`/spaces/${id}/`];
  if (kind === 'doc') return [`/documents/${id}`];
  if (kind === 'channel') return [`/channels/${id}`];
  return [];
}

export class RoomRegistry {
  private entries: Entry[] = [];

  /** Registers one subscription of a room; returns the function that removes it. */
  add(room: string, scope: RoomScope = {}): () => void {
    const entry: Entry = { room, scope };
    this.entries.push(entry);
    return () => {
      this.entries = this.entries.filter((candidate) => candidate !== entry);
    };
  }

  rooms(): string[] {
    return [...new Set(this.entries.map((entry) => entry.room))];
  }

  /** Fragments of the queries of `rooms` (the active rooms when omitted). */
  fragments(rooms: readonly string[] = this.rooms()): string[] {
    const wanted = new Set(rooms);
    const out = new Set<string>();
    for (const entry of this.entries) {
      if (!wanted.has(entry.room)) continue;
      for (const fragment of defaultFragments(entry.room)) out.add(fragment);
      for (const fragment of entry.scope.fragments ?? []) out.add(fragment);
    }
    return [...out];
  }

  /** The open rooms that an `access.revoked` message takes away, and the spaces they belong to. */
  revokedBy(revoked: RealtimeAccessRevoked): { rooms: string[]; spaceIds: string[] } {
    const rooms = new Set<string>();
    const spaces = new Set<string>();
    for (const { room, scope } of this.entries) {
      const spaceId = scope.spaceId ?? (room.startsWith('space:') ? room.slice(6) : undefined);
      const bySpace = revoked.spaceId !== null && spaceId === revoked.spaceId;
      const byRoom = revoked.scope === 'rooms' && revoked.rooms.includes(room);
      const byList = revoked.scope === 'space' && revoked.rooms.includes(room);
      if (bySpace || byRoom || byList) {
        rooms.add(room);
        if (spaceId) spaces.add(spaceId);
      }
    }
    return { rooms: [...rooms], spaceIds: [...spaces] };
  }
}
